import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Eye, Edit, Trash2, Wallet, LogIn } from 'lucide-react';
import { format, isToday, parseISO } from 'date-fns';
import { GuestLink } from '../crm/EntityLinks';
import { formatCurrency } from './utils';

export default function ReservationsTable({
  reservations,
  loading,
  channels,
  statusLabels,
  statusOptions,
  canSeeFinancials,
  onViewDetails,
  onEdit,
  onDelete,
  onStatusChange,
  onManageDeposit,
  onCheckIn
}) {
  if (loading) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="flex justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (reservations.length === 0) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="text-center py-12 text-muted-foreground">
            Aucune réservation trouvée
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Dates</th>
                <th>Source</th>
                <th>Pax</th>
                {canSeeFinancials && <th>Montant</th>}
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reservations.map((reservation) => {
                const channel = channels.find(c => c.code === reservation.source);
                const statusConfig = statusOptions.find(s => s.value === reservation.status);
                
                return (
                  <tr key={reservation.id} data-testid={`reservation-row-${reservation.id}`}>
                    <td>
                      <div>
                        <GuestLink guest={reservation.guest} />
                        {reservation.platform_ref && (
                          <p className="text-xs text-muted-foreground">#{reservation.platform_ref}</p>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="text-sm">
                        <p>{format(new Date(reservation.check_in), 'dd/MM/yyyy')}</p>
                        <p className="text-muted-foreground">
                          → {format(new Date(reservation.check_out), 'dd/MM/yyyy')}
                        </p>
                        <p className="text-xs text-muted-foreground">{reservation.nights} nuits</p>
                      </div>
                    </td>
                    <td>
                      <Badge 
                        style={{ 
                          backgroundColor: channel?.color ? `${channel.color}20` : undefined,
                          color: channel?.color,
                          borderColor: channel?.color
                        }}
                        variant="outline"
                      >
                        {channel?.label || reservation.source}
                      </Badge>
                    </td>
                    <td>
                      <div className="text-sm">
                        <p>{reservation.adults} ad.</p>
                        {reservation.children > 0 && (
                          <p className="text-muted-foreground">{reservation.children} enf.</p>
                        )}
                      </div>
                    </td>
                    {canSeeFinancials && (
                      <td>
                        <div>
                          <p className="font-semibold">
                            {formatCurrency(reservation.total_amount, reservation.currency)}
                          </p>
                          {reservation.currency === 'EUR' && reservation.total_amount_mad && (
                            <p className="text-xs text-blue-600">
                              ≈ {formatCurrency(reservation.total_amount_mad, 'MAD')}
                            </p>
                          )}
                          {reservation.commission_amount > 0 && (
                            <p className="text-xs text-red-600">
                              -{formatCurrency(reservation.commission_amount, reservation.currency)}
                            </p>
                          )}
                          {reservation.source === 'DIRECT' && reservation.deposit_amount > 0 && (
                            <p className="text-xs text-emerald-600">
                              Acompte: {formatCurrency(reservation.deposit_amount, reservation.currency)}
                            </p>
                          )}
                        </div>
                      </td>
                    )}
                    <td>
                      <Select
                        value={reservation.status}
                        onValueChange={(v) => onStatusChange(reservation.id, v)}
                      >
                        <SelectTrigger className="w-[140px] h-8">
                          <SelectValue>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-2 h-2 rounded-full" 
                                style={{ backgroundColor: statusConfig?.color || '#6B7280' }}
                              />
                              <span>{statusLabels[reservation.status] || reservation.status}</span>
                            </div>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map(status => (
                            <SelectItem key={status.value} value={status.value}>
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-2 h-2 rounded-full" 
                                  style={{ backgroundColor: status.color }}
                                />
                                {status.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => onViewDetails(reservation)}
                          title="Voir détails & passagers"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {/* Check-in button for confirmed arrivals today */}
                        {(reservation.status === 'CONFIRMED' || reservation.status === 'PENDING') && 
                         isToday(parseISO(reservation.check_in)) && onCheckIn && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => onCheckIn(reservation)}
                            title="Effectuer le check-in"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            data-testid={`checkin-btn-${reservation.id}`}
                          >
                            <LogIn className="w-4 h-4" />
                          </Button>
                        )}
                        {canSeeFinancials && reservation.source === 'DIRECT' && !reservation.is_blocked && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => onManageDeposit(reservation)}
                            title="Gérer l'acompte"
                            data-testid={`deposit-btn-${reservation.id}`}
                          >
                            <Wallet className="w-4 h-4 text-emerald-600" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => onEdit(reservation)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => onDelete(reservation.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
